import { DefaultEuler, EulerOrder } from "./Euler";
import { IMat4, Mat4 } from "./Mat4";
import { MathUtil } from "./MathUtil";
import { IVec3, Vec3 } from "./Vec3";

export interface IQuat {
  x: number;
  y: number;
  z: number;
  w: number;
}

export class Quat {
  static create(): IQuat {
    return {
      x: 0,
      y: 0,
      z: 0,
      w: 1
    }
  }

  static from(x: number, y: number, z: number, w: number): IQuat {
    return {
      x,
      y,
      z,
      w
    }
  }

  static conjugate(q: IQuat): IQuat {
    return Quat.from(
      -q.x,
      -q.y,
      -q.z,
      q.w
    );
  }

  // static diff(a: IQuat, b: IQuat): number {
  //   return Quat.multiply(a, Quat.invert(b));
  // }

  static dot(a: IQuat, b: IQuat): number {
    return a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  }

  static angleTo(a: IQuat, b: IQuat): number {

    return 2 * Math.acos(Math.abs(MathUtil.clamp(Quat.dot(a, b), - 1, 1)));

  }

  static invert(q: IQuat): IQuat {
    // assume unit length
    return Quat.conjugate(q);
  }

  static len(q: IQuat): number {
    return Math.sqrt(q.x ** 2 + q.y ** 2 + q.z ** 2 + q.w ** 2);
  }

  static normalize(q: IQuat): IQuat {
    const l = Quat.len(q);
    if (l === 0)
      return Quat.IDENTITY;

    const i = 1 / l;
    return {
      x: q.x * i,
      y: q.y * i,
      z: q.z * i,
      w: q.w * i,
    }
  }

  static equals(a: IQuat, b: IQuat): boolean {
    if (a === b) return true;
    if (!a && b) return false;
    if (!b && a) return false;
    return a.x === b.x && a.y === b.y && a.z === b.z && a.w === b.w;
  }

  static multiply(qa: IQuat, qb: IQuat): IQuat {
    const { x: qax, y: qay, z: qaz, w: qaw } = qa;
    const { x: qbx, y: qby, z: qbz, w: qbw } = qb;

    return Quat.from(
      qax * qbw + qaw * qbx + qay * qbz - qaz * qby,
      qay * qbw + qaw * qby + qaz * qbx - qax * qbz,
      qaz * qbw + qaw * qbz + qax * qby - qay * qbx,
      qaw * qbw - qax * qbx - qay * qby - qaz * qbz
    );
  }

  static premul(qa: IQuat, qb: IQuat): IQuat {
    return Quat.multiply(qb, qa);
  }

  static createFromRotationMatrix(m: IMat4): IQuat {
    // http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm
    // assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

    const m11 = m[0];
    const m12 = m[4];
    const m13 = m[8];
    const m21 = m[1];
    const m22 = m[5];
    const m23 = m[9];
    const m31 = m[2];
    const m32 = m[6];
    const m33 = m[10],

      trace = m11 + m22 + m33;

    if (trace > 0) {
      const s = 0.5 / Math.sqrt(trace + 1.0);
      return Quat.from((m32 - m23) * s, (m13 - m31) * s, (m21 - m12) * s, 0.25 / s);
    } else if (m11 > m22 && m11 > m33) {
      const s = 2.0 * Math.sqrt(1.0 + m11 - m22 - m33);
      return Quat.from(0.25 * s, (m12 + m21) / s, (m13 + m31) / s, (m32 - m23) / s);
    } else if (m22 > m33) {
      const s = 2.0 * Math.sqrt(1.0 + m22 - m11 - m33);
      return Quat.from((m12 + m21) / s, 0.25 * s, (m23 + m32) / s, (m13 - m31) / s);
    } else {
      const s = 2.0 * Math.sqrt(1.0 + m33 - m11 - m22);
      return Quat.from((m13 + m31) / s, (m23 + m32) / s, 0.25 * s, (m21 - m12) / s);
    }
  }

  static createFromEuler(x: number, y: number, z: number, order: EulerOrder = DefaultEuler): IQuat {
    const cos = Math.cos;
    const sin = Math.sin;

    const c1 = cos(x / 2);
    const c2 = cos(y / 2);
    const c3 = cos(z / 2);

    const s1 = sin(x / 2);
    const s2 = sin(y / 2);
    const s3 = sin(z / 2);

    switch (order) {
      case "XYZ": {
        return Quat.from(
          s1 * c2 * c3 + c1 * s2 * s3,
          c1 * s2 * c3 - s1 * c2 * s3,
          c1 * c2 * s3 + s1 * s2 * c3,
          c1 * c2 * c3 - s1 * s2 * s3
        )
      }
      case "YXZ": {
        return Quat.from(
          s1 * c2 * c3 + c1 * s2 * s3,
          c1 * s2 * c3 - s1 * c2 * s3,
          c1 * c2 * s3 - s1 * s2 * c3,
          c1 * c2 * c3 + s1 * s2 * s3
        )
      }
      default:
        throw new Error("EulerOrder not supported so far")
    }
  }

  static createFromUnitVectors(vFrom: IVec3, vTo: IVec3): IQuat {
    let r = Vec3.dot(vFrom, vTo) + 1;

    if (r < Number.EPSILON) {

      // vFrom and vTo point in opposite directions

      r = 0;

      if (Math.abs(vFrom.x) > Math.abs(vFrom.z)) {
        return Quat.normalize(Quat.from(-vFrom.y, vFrom.x, 0, r))
      } else {
        return Quat.normalize(Quat.from(0, -vFrom.z, vFrom.y, r))
      }

    } else {
      return Quat.normalize(Quat.from(vFrom.y * vTo.z - vFrom.z * vTo.y, vFrom.z * vTo.x - vFrom.x * vTo.z, vFrom.x * vTo.y - vFrom.y * vTo.x, r));
    }
  }

  static fromAxisAngle(axis: IVec3, angle: number): IQuat {
    const halfAngle = angle / 2, s = Math.sin(halfAngle);

    return {
      x: axis.x * s,
      y: axis.y * s,
      z: axis.z * s,
      w: Math.cos(halfAngle)
    }
  }

  static copySign(x: number, y: number): number {
    return Math.sign(x) === Math.sign(y) ? x : -x;
  }

  static toEuler(q: IQuat): IVec3 {
    const mat = Mat4.rotationFromQuat(q);
    return Mat4.calcEuler(mat);
    // // https://en.wikipedia.org/wiki/Conversion_between_quaternions_and_Euler_angles
    // // roll (x-axis rotation)
    // const sinr_cosp = 2 * (q.w * q.x + q.y * q.z);
    // const cosr_cosp = 1 - 2 * (q.x * q.x + q.y * q.y);
    // const x = Math.atan2(sinr_cosp, cosr_cosp);

    // // pitch (y-axis rotation)
    // const sinp = 2 * (q.w * q.y - q.z * q.x);
    // let y = Math.abs(sinp) >= 1 ? this.copySign(Math.PI / 2, sinp) : Math.asin(sinp);

    // // yaw (z-axis rotation)
    // const siny_cosp = 2 * (q.w * q.z + q.x * q.y);
    // const cosy_cosp = 1 - 2 * (q.y * q.y + q.z * q.z);
    // const z = Math.atan2(siny_cosp, cosy_cosp);

    // return {
    //   x, y, z
    // }

    // const sqw = q.w * q.w;
    // const sqx = q.x * q.x;
    // const sqy = q.y * q.y;
    // const sqz = q.z * q.z;
    // const unit = sqx + sqy + sqz + sqw; // if normalised is one, otherwise is correction factor

    // let heading: number;
    // let attitude: number;
    // let bank: number;

    // const test = q.x * q.y + q.z * q.w;
    // if (test > 0.499999 * unit) { // singularity at north pole
    //   heading = 2 * Math.atan2(q.x, q.w);
    //   attitude = Math.PI / 2;
    //   bank = 0;
    // }
    // else if (test < -0.499999 * unit) { // singularity at south pole
    //   heading = -2 * Math.atan2(q.x, q.w);
    //   attitude = -Math.PI / 2;
    //   bank = 0;
    // } else {
    //   heading = Math.atan2(2 * q.y * q.w - 2 * q.x * q.z, sqx - sqy - sqz + sqw);
    //   attitude = Math.asin(2 * test / unit);
    //   bank = Math.atan2(2 * q.x * q.w - 2 * q.y * q.z, -sqx + sqy - sqz + sqw)
    // }
    // return { x: bank, y: heading, z: attitude }
  }

  static IDENTITY: IQuat = Quat.from(0, 0, 0, 1);
}